import { useFormContext } from 'react-hook-form';

import { DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { SiemProtocol } from '@kirin/api-client/contracts/settings';
import { siemProtocolDefaultPort, siemProtocolLabelMap } from './constants';
import type { SiemFormValues } from './types';

interface DestinationFieldsProps {
  onSensitiveChange?: () => void;
}

export function DestinationFields({
  onSensitiveChange,
}: DestinationFieldsProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<SiemFormValues>();

  const protocol = watch('protocol');
  const isHec = protocol === SiemProtocol.Hec;
  // The checkbox is presented positively ("Enforce SSL", checked by default) and
  // maps to the inverse of the stored `insecureSkipVerify` flag (KIR-4285).
  const insecure = watch('insecureSkipVerify');

  const tcpLabel = siemProtocolLabelMap[SiemProtocol.Tcp];
  const udpLabel = siemProtocolLabelMap[SiemProtocol.Udp];
  const tcpPort = siemProtocolDefaultPort[SiemProtocol.Tcp];
  const udpPort = siemProtocolDefaultPort[SiemProtocol.Udp];
  const portHelperText = `Default: ${tcpPort} (${tcpLabel}). Common: ${udpPort} (${udpLabel})`;

  // Both groups stay mounted; the inactive one is only hidden. Unmounting would
  // drop the entered value (e.g. an Endpoint URL) when toggling protocol, so we
  // keep them registered and just toggle visibility.
  return (
    <>
      {/* HEC: full endpoint URL + bearer token — no host/port. */}
      <div
        data-testid="siem-destination-hec"
        className={cn('grid gap-4', !isHec && 'hidden')}
      >
        <div className="space-y-1">
          <Label htmlFor="siem-endpoint-url" className="font-semibold">
            Endpoint URL <span className="text-destructive">*</span>
          </Label>
          <Input
            id="siem-endpoint-url"
            placeholder="e.g., https://http-inputs-acme.splunkcloud.com:8088"
            aria-invalid={!!errors.endpointUrl}
            className="text-primary placeholder:text-foreground/50"
            {...register('endpointUrl', { onChange: onSensitiveChange })}
          />
          {errors.endpointUrl?.message && (
            <p className="text-xs text-destructive">
              {errors.endpointUrl.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="siem-hec-token" className="font-semibold">
            HEC Token <span className="text-destructive">*</span>
          </Label>
          <Input
            id="siem-hec-token"
            type="password"
            autoComplete="off"
            placeholder="Splunk HEC token"
            aria-invalid={!!errors.hecToken}
            className="text-primary placeholder:text-foreground/50"
            {...register('hecToken', { onChange: onSensitiveChange })}
          />
          {errors.hecToken?.message && (
            <p className="text-xs text-destructive">
              {errors.hecToken.message}
            </p>
          )}
        </div>
        <div className="flex items-start gap-2">
          <input
            id="siem-enforce-ssl"
            type="checkbox"
            className="mt-1 accent-primary"
            checked={!insecure}
            onChange={e => {
              // Positive framing: checked = enforce SSL = do NOT skip verify.
              setValue('insecureSkipVerify', !e.target.checked, {
                shouldDirty: true,
              });
              onSensitiveChange?.();
            }}
          />
          <div className="space-y-0.5">
            <Label htmlFor="siem-enforce-ssl" className="font-semibold">
              Enforce SSL certificate verification
            </Label>
            <p className="text-xs text-foreground/80">
              Recommended — Kirin validates the server certificate before
              sending the HEC token. Turn off only for self-signed or on-prem
              endpoints (e.g. a Splunk trial).
            </p>
            {insecure && (
              <p className="text-xs text-destructive">
                Insecure — SSL verification is off; the HEC token is sent over
                an unverified connection. Only for a trusted self-signed
                endpoint.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* syslog (tcp/udp): host + port. */}
      <div
        data-testid="siem-destination-syslog"
        className={cn('grid gap-4 sm:grid-cols-2', isHec && 'hidden')}
      >
        <div className="space-y-1">
          <Label htmlFor="siem-host" className="font-semibold">
            Destination Host / IP <span className="text-destructive">*</span>
          </Label>
          <Input
            id="siem-host"
            placeholder="e.g., siem.example.com"
            className="text-primary placeholder:text-foreground/50"
            {...register('host', { onChange: onSensitiveChange })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="siem-port" className="font-semibold">
            Port <span className="text-destructive">*</span>
          </Label>
          <Input
            id="siem-port"
            type="number"
            aria-invalid={!!errors.port}
            className="text-primary placeholder:text-foreground/50"
            {...register('port', {
              valueAsNumber: true,
              onChange: onSensitiveChange,
            })}
          />
          {errors.port?.message && (
            <p className="text-xs text-destructive">{errors.port.message}</p>
          )}
          <DialogDescription className="text-xs text-foreground/80">
            {portHelperText}
          </DialogDescription>
        </div>
      </div>
    </>
  );
}
